/**
 * Storage Factory
 * Creates and initializes the appropriate storage service based on configuration
 */

import { IContentStorage } from './contentStorage.interface';
import { LocalStorageService } from './localStorage.service';
import { CloudflareKVStorageService } from './cloudflareKV.service';

export type StorageType = 'local' | 'upstash' | 'cloudflare';

export interface StorageConfig {
  type: StorageType;
  config?: Record<string, any>;
}

/**
 * Create and initialize a storage service
 * @param storageConfig Configuration object or storage type string
 * @returns Initialized storage service
 */
export async function createStorageService(storageConfig?: StorageConfig | StorageType): Promise<IContentStorage> {
  // Default to local storage
  let type: StorageType = 'local';
  let config: Record<string, any> = {};

  // Parse configuration
  if (typeof storageConfig === 'string') {
    type = storageConfig;
  } else if (storageConfig) {
    type = storageConfig.type;
    config = storageConfig.config || {};
  }

  // Try to get from environment variables if not explicitly set
  if (!storageConfig && process.env) {
    type = (process.env.VUE_APP_STORAGE_TYPE as StorageType) || 'local';

    // Load environment-specific config
    if (type === 'upstash') {
      config = {
        restUrl: process.env.VUE_APP_UPSTASH_REST_URL,
        token: process.env.VUE_APP_UPSTASH_REST_TOKEN
      };
    } else if (type === 'cloudflare') {
      config = {
        accountId: process.env.VUE_APP_CLOUDFLARE_ACCOUNT_ID,
        namespaceId: process.env.VUE_APP_CLOUDFLARE_NAMESPACE_ID,
        apiToken: process.env.VUE_APP_CLOUDFLARE_API_TOKEN
      };
    }
  }

  // Create the appropriate storage service
  let storage: IContentStorage;

  switch (type) {
    case 'upstash':
      // Will be implemented in Phase 1
      console.warn('[StorageFactory] Upstash storage not yet implemented, falling back to local');
      storage = new LocalStorageService();
      break;

    case 'cloudflare':
      storage = new CloudflareKVStorageService();
      break;

    case 'local':
    default:
      storage = new LocalStorageService();
      break;
  }

  // Initialize the storage service
  await storage.init(config);

  console.log(`[StorageFactory] Initialized ${type} storage`);

  return storage;
}

/**
 * Singleton instance for the storage service
 * This ensures we only create one instance throughout the application
 */
let storageInstance: IContentStorage | null = null;

/**
 * Get the singleton storage instance
 * Creates one if it doesn't exist
 */
export async function getStorageService(): Promise<IContentStorage> {
  if (!storageInstance) {
    storageInstance = await createStorageService();
  } else {
    // Clear cache on existing instance to ensure fresh data on page refresh
    if ('clearCache' in storageInstance && typeof storageInstance.clearCache === 'function') {
      storageInstance.clearCache();
      console.log('[StorageFactory] Cache cleared on existing instance');
    }
  }
  return storageInstance;
}

/**
 * Reset the storage instance
 * Useful for testing or when switching storage types
 */
export function resetStorageService(): void {
  storageInstance = null;
  console.log('[StorageFactory] Storage service reset');
}
